import React, { useRef } from 'react';
import styles from './side-panel.module.scss';
import posed from 'react-pose';
import { useMatchMedia } from '../../hooks/use-match-media';
import { WindowLocation } from '@reach/router';
import { usePrevious } from '../../hooks/use-previous';

interface SidePanelProps {
  open: boolean;
  location: WindowLocation;
  full?: boolean;
  children: (previousLocation: WindowLocation) => React.ReactChild;
}

// Main container
export const SidePanelContainer: React.FC = props => {
  return (
    <div {...props} className={styles.container}>
      {props.children}
    </div>
  );
};

const SidePanelPose = posed.div({
  openMobile: {
    opacity: 1,
    width: '100%',
    height: '100%',
  },
  closedMobile: {
    opacity: 0,
    width: '100%',
    height: '100%',
  },
  openDesktop: {
    opacity: 1,
    width: `${styles.sidePanelWidth}`,
    transform: 'translate(0%, 0%)',
  },
  closedDesktop: {
    opacity: 0,
    width: `${styles.sidePanelWidth}`,
    transform: 'translate(100%, 0%)',
  },
  openDesktopFull: {
    opacity: 1,
    width: `calc(100% - ${styles.navBar})`,
    transform: 'translate(0%, 0%)',
  },
  closedDesktopFull: {
    opacity: 0,
    width: `calc(100% - ${styles.navBar})`,
    transform: 'translate(100%, 0%)',
  },
});

const SidePanelContentPose = posed.div({
  openMobile: {
    opacity: 1,
  },
  closedMobile: {
    opacity: 0,
  },
  openDesktop: {
    opacity: 1,
  },
  closedDesktop: {
    opacity: 0,
  },
  openDesktopFull: {
    opacity: 1,
  },
  closedDesktopFull: {
    opacity: 0,
  },
});

// Side Panel
export const SidePanel: React.FC<SidePanelProps> = ({ children, full, open, location }) => {
  const fullClass = full ? styles.full : '';
  const openClass = open ? styles.open : '';
  const isDesktop = useMatchMedia(`(min-width: ${styles.medium})`);
  const previousLocation = usePrevious(location);
  const sidePanelEL = useRef<HTMLDivElement>(null);

  if (previousLocation && previousLocation.pathname !== location.pathname && sidePanelEL.current) {
    if (sidePanelEL.current.scrollTo) {
      sidePanelEL.current.scrollTo(0, 0);
    }
  }

  // TODO: Tidy up
  const activePose = isDesktop
    ? open
      ? full
        ? 'openDesktopFull'
        : 'openDesktop'
      : full
      ? 'closedDesktopFull'
      : 'closedDesktop'
    : open
    ? 'openMobile'
    : 'closedMobile';

  return (
    <SidePanelPose
      className={`${styles.panel} ${fullClass} ${openClass}`}
      isDesktop={isDesktop}
      pose={activePose}
      ref={sidePanelEL}
    >
      <SidePanelContentPose className={styles.content} pose={activePose}>
        {children(previousLocation)}
      </SidePanelContentPose>
    </SidePanelPose>
  );
};
